import React from 'react';

const HIGHLIGHT_CLASS = 'rounded bg-yellow-200 px-0 text-gray-950 dark:bg-yellow-500/40 dark:text-yellow-50';

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getSearchTerms(query?: string): string[] {
  if (!query) return [];

  const seen = new Set<string>();

  return query
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .filter((term) => {
      const normalized = term.toLowerCase();
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
}

function createHighlightRegExp(query?: string): RegExp | null {
  const terms = getSearchTerms(query);

  if (terms.length === 0) {
    return null;
  }

  return new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
}

export function highlightText(text: string, query?: string): React.ReactNode {
  const highlightRegExp = createHighlightRegExp(query);

  if (!highlightRegExp) {
    return text;
  }

  return text.split(highlightRegExp).map((part, index) => {
    if (!part) return null;

    if (highlightRegExp.test(part)) {
      highlightRegExp.lastIndex = 0;
      return (
        <mark key={`${part}-${index}`} className={HIGHLIGHT_CLASS}>
          {part}
        </mark>
      );
    }

    highlightRegExp.lastIndex = 0;
    return part;
  });
}

export function highlightHtml(html: string, query?: string): string {
  const highlightRegExp = createHighlightRegExp(query);

  if (!highlightRegExp || typeof document === 'undefined') {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    textNodes.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const value = textNode.nodeValue ?? '';
    if (!highlightRegExp.test(value)) {
      highlightRegExp.lastIndex = 0;
      return;
    }

    highlightRegExp.lastIndex = 0;
    const fragment = document.createDocumentFragment();

    value.split(highlightRegExp).forEach((part) => {
      if (!part) return;

      if (highlightRegExp.test(part)) {
        const mark = document.createElement('mark');
        mark.className = HIGHLIGHT_CLASS;
        mark.textContent = part;
        fragment.appendChild(mark);
      } else {
        fragment.appendChild(document.createTextNode(part));
      }

      highlightRegExp.lastIndex = 0;
    });

    textNode.parentNode?.replaceChild(fragment, textNode);
  });

  return container.innerHTML;
}
